import {
	type Diet,
	type ImageRef,
	type Item,
	type ItemTag,
	MAX_ITEM_SUGGESTIONS,
	shortCodeSchema,
	type UpsertItemInput,
	upsertItemInputSchema,
	uuidv7,
} from "@luckymenu/core";
import { moveInList } from "./cache";
import { type FormErrors, paiseToRupees, rupeesToPaise, zodProblems } from "./form-errors";

/**
 * The item form as both apps edit it: text as typed, prices in rupees.
 * `itemDraftToInput` turns it into `staff.catalog.upsertItem`'s input.
 */

export type VariantDraft = {
	id: string;
	name: string;
	/** Rupees as typed. */
	price: string;
	isDefault: boolean;
	isAvailable: boolean;
};

export type ItemDraft = {
	id: string;
	categoryId: string;
	name: string;
	namePrint: string;
	description: string;
	/** Null until chosen: a new item has no diet mark by default. */
	diet: Diet | null;
	/** Rupees as typed. Unused while the item has sizes. */
	price: string;
	/** Sizes such as Half and Full. Empty for one price. */
	variants: VariantDraft[];
	modifierGroupIds: string[];
	/** "Goes well with": up to four other dishes, in order (P9-T11). */
	suggestedItemIds: string[];
	image: ImageRef | null;
	tags: ItemTag[];
	shortCode: string;
	/** Null: the default tax class. */
	taxClassId: string | null;
	/** Null: the first kitchen station. */
	kitchenStationId: string | null;
	/** False: hidden from the menu. */
	isActive: boolean;
	isAvailable: boolean;
};

export const ITEM_LIMITS = {
	name: 80,
	namePrint: 40,
	description: 500,
	variantName: 40,
	variants: 20,
} as const;

export function newItemDraft(categoryId: string, id: string = uuidv7()): ItemDraft {
	return {
		id,
		categoryId,
		name: "",
		namePrint: "",
		description: "",
		diet: null,
		price: "",
		variants: [],
		modifierGroupIds: [],
		suggestedItemIds: [],
		image: null,
		tags: [],
		shortCode: "",
		taxClassId: null,
		kitchenStationId: null,
		isActive: true,
		isAvailable: true,
	};
}

export function itemToDraft(item: Item): ItemDraft {
	return {
		id: item.id,
		categoryId: item.categoryId,
		name: item.name,
		namePrint: item.namePrint ?? "",
		description: item.description ?? "",
		diet: item.diet,
		price: paiseToRupees(item.basePricePaise),
		variants: item.variants.map((v) => ({
			id: v.id,
			name: v.name,
			price: paiseToRupees(v.pricePaise),
			isDefault: v.isDefault,
			isAvailable: v.isAvailable,
		})),
		modifierGroupIds: [...item.modifierGroupIds],
		suggestedItemIds: [...item.suggestedItemIds],
		image: item.image,
		tags: [...item.tags],
		shortCode: item.shortCode ?? "",
		taxClassId: item.taxClassId,
		kitchenStationId: item.kitchenStationId,
		isActive: item.isActive,
		isAvailable: item.isAvailable,
	};
}

// ---------------------------------------------------------------------------
// Edits. Each returns a new draft.
// ---------------------------------------------------------------------------

/**
 * Adds a size. The first turns the single price into two sizes: the first
 * keeps the price, the second is empty.
 */
export function addVariant(draft: ItemDraft): ItemDraft {
	if (draft.variants.length >= ITEM_LIMITS.variants) return draft;
	const blank = (price = "", isDefault = false): VariantDraft => ({
		id: uuidv7(),
		name: "",
		price,
		isDefault,
		isAvailable: true,
	});
	if (draft.variants.length === 0)
		return { ...draft, variants: [blank(draft.price, true), blank()] };
	return { ...draft, variants: [...draft.variants, blank()] };
}

/**
 * Removes a size. Down to one, the item goes back to a single price, the
 * remaining size's.
 */
export function removeVariant(draft: ItemDraft, index: number): ItemDraft {
	const variants = draft.variants.filter((_, i) => i !== index);
	if (variants.length <= 1)
		return { ...draft, variants: [], price: variants[0]?.price ?? draft.price };
	if (!variants.some((v) => v.isDefault) && variants[0])
		variants[0] = { ...variants[0], isDefault: true };
	return { ...draft, variants };
}

export function updateVariant(
	draft: ItemDraft,
	index: number,
	change: Partial<Omit<VariantDraft, "id" | "isDefault">>,
): ItemDraft {
	return {
		...draft,
		variants: draft.variants.map((v, i) => (i === index ? { ...v, ...change } : v)),
	};
}

/** The size the item sheet and the item picker choose first. */
export function setDefaultVariant(draft: ItemDraft, index: number): ItemDraft {
	return { ...draft, variants: draft.variants.map((v, i) => ({ ...v, isDefault: i === index })) };
}

export function moveVariant(draft: ItemDraft, from: number, to: number): ItemDraft {
	return { ...draft, variants: moveInList(draft.variants, from, to) };
}

export function toggleTag(draft: ItemDraft, tag: ItemTag): ItemDraft {
	return {
		...draft,
		tags: draft.tags.includes(tag) ? draft.tags.filter((t) => t !== tag) : [...draft.tags, tag],
	};
}

/** Offers the add-on group, after the others, or stops offering it. */
export function toggleModifierGroup(draft: ItemDraft, groupId: string): ItemDraft {
	const ids = draft.modifierGroupIds;
	return {
		...draft,
		modifierGroupIds: ids.includes(groupId) ? ids.filter((g) => g !== groupId) : [...ids, groupId],
	};
}

/**
 * Suggests the dish with this one, after the others, or stops suggesting
 * it. At most four; never the item itself.
 */
export function toggleSuggestion(draft: ItemDraft, itemId: string): ItemDraft {
	const ids = draft.suggestedItemIds ?? [];
	if (ids.includes(itemId)) return { ...draft, suggestedItemIds: ids.filter((i) => i !== itemId) };
	if (itemId === draft.id || ids.length >= MAX_ITEM_SUGGESTIONS) return draft;
	return { ...draft, suggestedItemIds: [...ids, itemId] };
}

// ---------------------------------------------------------------------------
// Validation.
// ---------------------------------------------------------------------------

export type ItemFormResult =
	| { ok: true; input: UpsertItemInput }
	| { ok: false; errors: FormErrors };

/**
 * The upsert input, or every problem found. `original` is the saved item
 * (null for a new one): the photo is sent only when it changed.
 */
export function itemDraftToInput(draft: ItemDraft, original: Item | null): ItemFormResult {
	const errors: FormErrors = {};
	const name = draft.name.trim();
	if (name === "") errors.name = "required";
	else if (name.length > ITEM_LIMITS.name) errors.name = "tooLong";
	if (draft.namePrint.trim().length > ITEM_LIMITS.namePrint) errors.namePrint = "tooLong";
	if (draft.description.trim().length > ITEM_LIMITS.description) errors.description = "tooLong";
	if (!draft.diet) errors.diet = "diet";
	if (draft.shortCode.trim() !== "" && !shortCodeSchema.safeParse(draft.shortCode).success)
		errors.shortCode = "shortCode";

	let basePricePaise: number | undefined;
	const variants: NonNullable<UpsertItemInput["variants"]> = [];
	if (draft.variants.length === 0) {
		const price = rupeesToPaise(draft.price);
		if ("problem" in price) errors.price = price.problem;
		else basePricePaise = price.paise;
	} else {
		draft.variants.forEach((v, i) => {
			const variantName = v.name.trim();
			if (variantName === "") errors[`variants.${i}.name`] = "required";
			else if (variantName.length > ITEM_LIMITS.variantName)
				errors[`variants.${i}.name`] = "tooLong";
			const price = rupeesToPaise(v.price);
			if ("problem" in price) errors[`variants.${i}.price`] = price.problem;
			else
				variants.push({
					id: v.id,
					name: variantName,
					pricePaise: price.paise,
					isDefault: v.isDefault,
					isAvailable: v.isAvailable,
				});
		});
	}
	if (Object.keys(errors).length > 0) return { ok: false, errors };

	const imageChanged = (draft.image?.id ?? null) !== (original?.image?.id ?? null);
	const input: UpsertItemInput = {
		id: draft.id,
		categoryId: draft.categoryId,
		name,
		namePrint: draft.namePrint.trim(),
		description: draft.description.trim(),
		diet: draft.diet as Diet,
		...(basePricePaise !== undefined ? { basePricePaise } : {}),
		taxClassId: draft.taxClassId,
		kitchenStationId: draft.kitchenStationId,
		...(imageChanged ? { imageFileId: draft.image?.id ?? null } : {}),
		shortCode: draft.shortCode.trim(),
		tags: draft.tags,
		isActive: draft.isActive,
		isAvailable: draft.isAvailable,
		variants,
		modifierGroupIds: draft.modifierGroupIds,
		// A draft kept on the device from before Phase 9 has none.
		suggestedItemIds: draft.suggestedItemIds ?? [],
	};
	const parsed = upsertItemInputSchema.safeParse(input);
	if (!parsed.success) return { ok: false, errors: zodProblems(parsed.error) };
	return { ok: true, input };
}
